import * as z from "zod";
import { flag, pagination } from "../index";
import { invitationKinds, linesheetStatuses } from "./enums";

// Invitations to the brands to load their linesheet for a sales campaign (50-api/catalog.md §2.4).

const invitationFilters = {
  /** Code of the season of the campaign. */
  season: z.string().max(20).optional(),
  salesCampaignId: z.uuid().optional(),
  /**
   * Still waiting for the brand's linesheet a cadence after the last reminder of the daily job (B-97, ADR-245):
   * Operations' queue, which calls the brand or sends the invitation again by hand.
   */
  remindersOver: flag.optional(),
};

/** The brands invited to the campaigns, the campaign that starts last first. */
export const listLinesheetInvitationsInput = z.object({
  ...invitationFilters,
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListLinesheetInvitationsInput = z.input<typeof listLinesheetInvitationsInput>;

/** How many invitations the list has with the same filters, and the oldest untouched among them (ADR-086). */
export const countLinesheetInvitationsInput = z.object(invitationFilters);
export type CountLinesheetInvitationsInput = z.input<typeof countLinesheetInvitationsInput>;

/** Invites a brand to a campaign, or reminds it: the brand joins the campaign with its first invitation (ADR-076). */
export const sendLinesheetInvitationInput = z.object({
  brandOrgId: z.uuid(),
  salesCampaignId: z.uuid(),
});
export type SendLinesheetInvitationInput = z.input<typeof sendLinesheetInvitationInput>;

export const linesheetInvitationDto = z
  .object({
    id: z.uuid(),
    brandOrgId: z.uuid(),
    brand: z.string(),
    salesCampaign: z.object({
      id: z.uuid(),
      name: z.string(),
      season: z.string().nullable(),
      startDate: z.iso.date().nullable(),
    }),
    /** Null for a brand that joined the campaign and was not invited yet. */
    lastEmailSentAt: z.iso.datetime().nullable(),
    /** First and last name of who sent the last email by hand. */
    sentBy: z.string().nullable(),
    /** The daily job sent the last email, not a person. */
    automatic: z.boolean(),
    /** The most advanced linesheet of the brand for the season of the campaign, archived ones aside. */
    linesheetStatus: z.enum(linesheetStatuses).nullable(),
    /** The day the brand is asked to submit by; null for a campaign without a start date. */
    deadline: z.iso.date().nullable(),
    /**
     * What the daily job sends next and when; null once the brand submitted, the campaign started or the reminders are
     * over.
     */
    next: z.object({ kind: z.enum(invitationKinds), on: z.iso.date() }).nullable(),
    /** Reminders the daily job sent since the last email by hand (B-97, ADR-245). */
    reminderCount: z.number().int(),
    /** Still waiting a cadence after the last reminder: in Operations' queue (`remindersOver`). */
    remindersOver: z.boolean(),
  })
  .meta({ id: "LinesheetInvitation" });
